// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// FRONT-END-ONLY SEED DATA (alarm detail · "Floor plan" panel), gated behind
// REACT_APP_MOCK_FLOOR_PLAN — see components/equipment/AssetLineupPanel.tsx.
// Mirrors REACT_APP_MOCK_WEATHER and REACT_APP_FORCE_ALARM_MOCKED_DATA.
//
// Shaped as digital-twin `Entity`/`Relationship` rows — the same payload the
// details endpoint returns — so the seed runs through the real
// buildAssetLineup() and the real panel. Mock mode therefore renders only what
// the live path could render, and every cell state below is reachable with real
// data. See docs/design/alarm-detail-floor-plan-widget.md §8 for why the
// Microcks graph cannot produce this lineup today.

import type { Entity, Relationship } from '../types/digitalTwin';

/**
 * Whether the "Floor plan" panel renders this seed instead of the live twin.
 *
 * Defaults off. Read per call rather than captured at module scope so tests can
 * flip it without re-importing the module graph.
 */
export function shouldMockFloorPlan(): boolean {
  return process.env.REACT_APP_MOCK_FLOOR_PLAN === 'true';
}

if (shouldMockFloorPlan() && process.env.NODE_ENV === 'development') {
  console.warn(
    '[Helios] REACT_APP_MOCK_FLOOR_PLAN is ON — the alarm detail "Floor plan" ' +
      'panel shows seeded data, not the digital twin. Remove it from .env.local ' +
      'to restore live data.'
  );
}

/** The alarming asset the seeded lineup is centred on. */
export const MOCK_FLOOR_PLAN_FOCUS_ID = 'mock-ch-2';

const HEADER_ID = 'mock-chiller-plant';

const chiller = (
  n: number,
  status: string | undefined,
  supplyTempF?: number
): Entity & { state: NonNullable<Entity['state']> } => ({
  id: `mock-ch-${n}`,
  type: 'Chiller',
  attributes: { label: `CH-${n}` },
  state: {
    updatedAt: '2026-08-12T09:15:00Z',
    ...(status ? { status: { value: status } } : {}),
    ...(supplyTempF === undefined
      ? {}
      : {
          SupplyTemperature: {
            value: supplyTempF,
            unit: '°F',
            quantityKind: 'Temperature',
            timestamp: '2026-08-12T09:15:00Z',
          },
        }),
  },
});

/**
 * Six chillers on one header, one per cell state the panel can produce, in the
 * label order the panel sorts into — so the rendered grid reads top-left to
 * bottom-right as: reading, fault (focus), warning reading, standby, healthy
 * with no reading, no status at all. CH-7/CH-8 exist only to drive the "+N"
 * overflow chip at the default six-cell cap.
 */
export const MOCK_FLOOR_PLAN_ENTITIES: Entity[] = [
  { id: HEADER_ID, type: 'Chiller_Plant_Header', attributes: { label: 'Chiller Plant' } },
  chiller(1, 'operational', 46.4),
  // Focus. Carries a plausible last reading the panel must suppress in favour
  // of FLT — a faulted chiller's final supply temperature reads as if it were
  // fine.
  chiller(2, 'fault', 47.1),
  chiller(3, 'degraded', 58.6),
  chiller(4, 'offline'),
  chiller(5, 'operational'),
  chiller(6, undefined),
  chiller(7, 'operational', 45.9),
  chiller(8, 'operational', 46.2),
];

export const MOCK_FLOOR_PLAN_RELATIONSHIPS: Relationship[] = MOCK_FLOOR_PLAN_ENTITIES.filter(
  e => e.id !== HEADER_ID
).map(e => ({
  id: `${HEADER_ID}-feeds-${e.id}`,
  source: HEADER_ID,
  target: e.id,
  type: 'suppliesPowerTo',
}));
